import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
interface SplashScreenProps {
  onComplete: () => void;
}
export function SplashScreen({
  onComplete
}: SplashScreenProps) {
  useEffect(() => {
    const timer = setTimeout(onComplete, 4500);
    return () => clearTimeout(timer);
  }, [onComplete]);
  return <motion.div initial={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.5
  }} className="fixed inset-0 z-50 bg-black flex items-center justify-center overflow-hidden" data-id="element-186">
      {/* Animated grid background */}
      <div className="absolute inset-0 opacity-10" data-id="element-187">
        <motion.div className="absolute inset-0" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1
      }} style={{
        backgroundImage: `
              linear-gradient(rgba(252, 211, 77, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(252, 211, 77, 0.3) 1px, transparent 1px)
            `,
        backgroundSize: '50px 50px'
      }} data-id="element-188" />
      </div>

      {/* Main animation container */}
      <div className="relative w-full max-w-5xl h-[500px] flex items-end justify-center pb-20" data-id="element-189">
        {/* Ground line - everything sits on this */}
        <motion.div className="absolute bottom-20 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-yellow-400/40 to-transparent" initial={{
        scaleX: 0
      }} animate={{
        scaleX: 1
      }} transition={{
        duration: 1,
        delay: 0.3
      }} data-id="element-190" />

        {/* Warehouse building - wider and shorter with flat roof */}
        <motion.svg width="700" height="300" viewBox="0 0 700 300" className="absolute bottom-20" style={{
        left: '50%',
        transform: 'translateX(-50%)'
      }} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} data-id="element-191">
          {/* Main warehouse structure - wider and shorter */}
          <motion.rect x="50" y="150" width="600" height="150" fill="none" stroke="rgba(252, 211, 77, 0.3)" strokeWidth="3" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          duration: 1.5,
          ease: 'easeInOut'
        }} data-id="element-192" />

          {/* Flat roof with slight angle - much less pronounced */}
          <motion.polygon points="30,150 350,120 670,150" fill="none" stroke="rgba(252, 211, 77, 0.3)" strokeWidth="3" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          duration: 1.2,
          delay: 0.3
        }} data-id="element-193" />

          {/* Warehouse door frame - at ground level, centered */}
          <motion.rect x="300" y="200" width="100" height="100" fill="none" stroke="rgba(252, 211, 77, 0.5)" strokeWidth="3" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5,
          delay: 1
        }} data-id="element-194" />

          {/* Left door - opens left */}
          <motion.rect x="300" y="200" width="50" height="100" fill="rgba(20, 20, 20, 0.9)" stroke="rgba(252, 211, 77, 0.7)" strokeWidth="2" initial={{
          x: 300
        }} animate={{
          x: 270
        }} transition={{
          duration: 0.7,
          delay: 1.8,
          ease: 'easeInOut'
        }} data-id="element-195" />

          {/* Right door - opens right */}
          <motion.rect x="350" y="200" width="50" height="100" fill="rgba(20, 20, 20, 0.9)" stroke="rgba(252, 211, 77, 0.7)" strokeWidth="2" initial={{
          x: 350
        }} animate={{
          x: 380
        }} transition={{
          duration: 0.7,
          delay: 1.8,
          ease: 'easeInOut'
        }} data-id="element-196" />

          {/* Windows - distributed across the wider warehouse */}
          <motion.rect x="120" y="180" width="40" height="30" fill="rgba(252, 211, 77, 0.2)" initial={{
          opacity: 0
        }} animate={{
          opacity: [0, 1, 1]
        }} transition={{
          duration: 0.5,
          delay: 1.2
        }} data-id="element-197" />
          <motion.rect x="200" y="180" width="40" height="30" fill="rgba(252, 211, 77, 0.2)" initial={{
          opacity: 0
        }} animate={{
          opacity: [0, 1, 1]
        }} transition={{
          duration: 0.5,
          delay: 1.25
        }} data-id="element-198" />
          <motion.rect x="460" y="180" width="40" height="30" fill="rgba(252, 211, 77, 0.2)" initial={{
          opacity: 0
        }} animate={{
          opacity: [0, 1, 1]
        }} transition={{
          duration: 0.5,
          delay: 1.3
        }} data-id="element-199" />
          <motion.rect x="540" y="180" width="40" height="30" fill="rgba(252, 211, 77, 0.2)" initial={{
          opacity: 0
        }} animate={{
          opacity: [0, 1, 1]
        }} transition={{
          duration: 0.5,
          delay: 1.35
        }} data-id="element-200" />

          {/* Warehouse name plate */}
          <motion.rect x="280" y="160" width="140" height="25" fill="rgba(252, 211, 77, 0.1)" stroke="rgba(252, 211, 77, 0.3)" strokeWidth="1" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5,
          delay: 1.5
        }} data-id="element-201" />

          {/* Roof edge detail */}
          <motion.line x1="50" y1="150" x2="650" y2="150" stroke="rgba(252, 211, 77, 0.2)" strokeWidth="1" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          duration: 1,
          delay: 0.8
        }} data-id="element-202" />
        </motion.svg>

        {/* Real truck image with yellow color filter */}
        <motion.img src="https://uploadthingy.s3.us-west-1.amazonaws.com/2JURVQejdcjyWM4EVfgz3Z/TRUCK.png" alt="Truck" className="absolute bottom-20 h-16 md:h-20" style={{
        filter: 'brightness(0) saturate(100%) invert(85%) sepia(58%) saturate(500%) hue-rotate(359deg) brightness(102%) contrast(95%)'
      }} initial={{
        x: -400,
        opacity: 0
      }} animate={{
        x: [-400, -200, 150],
        opacity: [0, 1, 1]
      }} transition={{
        duration: 3.5,
        delay: 0.8,
        ease: 'easeInOut',
        times: [0, 0.3, 1]
      }} data-id="element-203" />

        {/* Ambient light glow */}
        <motion.div className="absolute inset-0 flex items-center justify-center pointer-events-none" initial={{
        opacity: 0
      }} animate={{
        opacity: [0, 0.2, 0]
      }} transition={{
        duration: 2,
        delay: 2.5
      }} data-id="element-204">
          <div className="w-[600px] h-[400px] bg-yellow-400/20 rounded-full blur-3xl" data-id="element-205" />
        </motion.div>
      </div>
    </motion.div>;
}